import React from "react";

import { FormBuilderHook, FormBuilderState, UseFormBuilderProps } from "./FormBuilder.types";
import { buildState, cleanState, populatePayload, validateForm } from "./lib/useFormUtils";

/**
 * Custom hook to manage form state and handle validation.
 *
 * @param onSubmit Callback function to execute on form submission.
 * @param entity Initial values for the form fields.
 * @param form Configuration for the form fields.
 * @param formRef Ref object to expose form methods externally.
 * @param resetFormOnSubmit Flag to reset the form to initial state on submit.
 * @param submitFormOnEnter Flag to submit the form on Enter key press.
 * @param onError Callback function to execute when a validation error occurs.
 * @returns Object containing form state and handler functions.
 */
export function useFormBuilder<T>({
  onSubmit,
  entity = {} as T,
  form,
  formRef,
  resetFormOnSubmit,
  submitFormOnEnter,
  onError,
}: UseFormBuilderProps<T>): FormBuilderHook<T> {
  // Default callback for form submission
  const onSubmitCb = onSubmit || (() => {});

  // State initialization for form state
  const [formState, setFormState] = React.useState<FormBuilderState<T>>(() => buildState(form, entity));
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  /**
   * Submits the form. Performs validation and calls the onSubmit callback if valid.
   */
  const submit = React.useCallback(async () => {
    setIsLoading(true);
    const { validatedState, hasErrors } = validateForm(form, formState, true);
    setFormState(validatedState);
    if (hasErrors) {
      onError && onError();
      setIsLoading(false);
      return;
    }
    const payload: T = populatePayload(form, formState);
    try {
      await onSubmitCb(payload);
    } catch (error) {
      console.error("Form submission error:", error);
      throw new Error(error as any);
    } finally {
      setIsLoading(false);
    }
    if (resetFormOnSubmit) {
      setFormState(cleanState(form));
    }
  }, [form, formState, onSubmitCb, onError, resetFormOnSubmit]);
  /**
   * Handles form submission events.
   * Prevents default form submission behavior and triggers submit function.
   *
   * @param event The form submission event.
   */
  const onFormSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitFormOnEnter) {
        submit();
      }
    },
    [submitFormOnEnter, submit]
  );

  /**
   * Effect to update the formRef with a method to externally set the form state.
   */
  React.useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formRef.current,
        setState: (state) => {
          setFormState(buildState(form, state));
        },
      };
    }
  }, [formRef, form]);

  /**
   * Handles value changes for form fields. Triggers validation and updates form state.
   *
   * @param key The key of the form field that has changed.
   * @param value The new value of the form field.
   */
  const onValueChange = React.useCallback(
    <PValue>(key: keyof T, value: PValue) => {
      setFormState((prevState) => {
        if (!prevState[key]) return prevState;
        const newState = { ...prevState };
        newState[key] = {
          ...prevState[key],
          value: typeof value === "string" ? value.trimStart() : value,
          isTouched: true,
        };
        return validateForm(form, newState).validatedState;
      });
    },
    [form]
  );

  return { onFormSubmit, submit, onValueChange, formState, isLoading };
}
